import { supabase } from "@/integrations/supabase/client";

// Function to generate an image using OpenAI's DALL-E 3 model
export async function generateImage(prompt: string): Promise<string> {
  console.log("Generating image with server-side OpenAI key via proxy.");
  try {
    const { data, error } = await supabase.functions.invoke('openai-proxy', {
      body: {
        endpoint: 'images/generations',
        body: {
          model: "dall-e-3",
          prompt: prompt,
          n: 1,
          size: "1024x1024",
          response_format: "url"
        }
      }
    });

    if (error) {
      console.error('Error invoking OpenAI proxy function for image:', error);
      throw new Error(error.message || 'Failed to generate image via proxy');
    }

    if (!data || data.error) {
      const errorMessage = data?.error?.message || 'Failed to generate image via proxy';
      if (errorMessage.includes('API key')) {
        throw new Error("Invalid API key configuration on server. Please contact the administrator.");
      } else if (errorMessage.includes('billing')) {
        throw new Error("OpenAI billing issue on server. Please contact the administrator.");
      } else if (errorMessage.includes('safety')) {
        throw new Error("Your prompt was rejected by OpenAI's safety system. Please modify your prompt.");
      }
      throw new Error(errorMessage);
    }
    return data.data[0].url;
  } catch (error) {
    console.error('Error generating image via proxy:', error);
    throw error;
  }
}

interface AIConfig {
  temperature?: number;
  max_tokens?: number;
  top_p?: number;
}

function isGpt5Model(model: string): boolean {
  return /^gpt-5/i.test(model);
}

function isReasoningTokenExhaustion(data: any): boolean {
  const finishReason = data?.choices?.[0]?.finish_reason;
  const completionTokens = data?.usage?.completion_tokens;
  const reasoningTokens = data?.usage?.completion_tokens_details?.reasoning_tokens;

  return finishReason === 'length' && Boolean(completionTokens) && completionTokens === reasoningTokens;
}

function extractOpenAIResponseText(data: any): string {
  const directContent = data?.choices?.[0]?.message?.content;
  if (typeof directContent === 'string') {
    return directContent;
  }

  if (Array.isArray(directContent)) {
    const combined = directContent
      .map((part: any) => {
        if (typeof part === 'string') return part;
        if (typeof part?.text === 'string') return part.text;
        if (typeof part?.content === 'string') return part.content;
        return '';
      })
      .filter(Boolean)
      .join('\n')
      .trim();

    if (combined) return combined;
  }

  if (typeof data?.output_text === 'string' && data.output_text.trim()) {
    return data.output_text;
  }

  if (Array.isArray(data?.output)) {
    const combined = data.output
      .flatMap((item: any) => Array.isArray(item?.content) ? item.content : [])
      .map((part: any) => {
        if (typeof part?.text === 'string') return part.text;
        if (typeof part?.content === 'string') return part.content;
        return '';
      })
      .filter(Boolean)
      .join('\n')
      .trim();

    if (combined) return combined;
  }

  if (typeof data?.choices?.[0]?.message?.refusal === 'string' && data.choices[0].message.refusal.trim()) {
    return data.choices[0].message.refusal;
  }

  return '';
}

// Function to generate chat completions using OpenAI API
export async function generateChatCompletion(
  messages: Array<{ role: string; content: string | Array<{type: string, text?: string, image_url?: {url: string, detail: string}}> }>,
  model: string = "gpt-4o",
  chatId?: string, // <-- Add chatId parameter
  config?: AIConfig
): Promise<{ content: string; tokensUsed: number }> {
  let retries = 3;
  let lastError: any = null;
  let backoffDelay = 1000;

  while (retries > 0) {
    try {
      console.log(`Sending request to OpenAI via proxy with model: ${model} using server key.`);
      // DETAILED LOGGING FOR PROXY
      console.log('[OpenAI Lib - Proxy] Messages payload:', JSON.stringify(messages, null, 2));
      messages.forEach((msg, index) => {
        if (Array.isArray(msg.content)) {
          console.log(`[OpenAI Lib - Proxy] Message ${index} (role: ${msg.role}) content parts:`);
          (msg.content as Array<any>).forEach(part => console.log(`[OpenAI Lib - Proxy]   Part type: ${part.type}, text: ${part.text ? part.text.substring(0,30)+'...' : 'N/A'}, image_url: ${part.image_url ? part.image_url.url.substring(0,30)+'...' : 'N/A'}`));
        } else {
          console.log(`[OpenAI Lib - Proxy] Message ${index} (role: ${msg.role}) content: ${typeof msg.content === 'string' ? msg.content.substring(0,50)+'...' : 'Non-string content'}`);
        }
      });

      const requestBody: Record<string, unknown> = {
        model,
        messages,
        max_tokens: config?.max_tokens ?? 1500,
      };

      if (!isGpt5Model(model)) {
        requestBody.temperature = config?.temperature;
        requestBody.top_p = config?.top_p;
      } else {
        requestBody.reasoning_effort = 'low';
      }

      const invokeCompletion = async (body: Record<string, unknown>) => {
        const { data, error: proxyError } = await supabase.functions.invoke('openai-proxy', {
          body: {
            endpoint: 'chat/completions',
            body,
            chat_id: chatId
          }
        });

        if (proxyError) {
          console.error('Error invoking OpenAI proxy function for chat:', proxyError);
          throw new Error(proxyError.message || 'Failed to get response from OpenAI via proxy');
        }

        if (!data || data.error) {
          const errorMessage = data?.error?.message || 'Failed to get response from OpenAI via proxy';
          console.error('OpenAI API response error via proxy:', data?.error);
          if (errorMessage.includes('503') || errorMessage.includes('429')) {
            throw new Error(`OpenAI service temporarily unavailable. Retrying...`);
          }
          if (errorMessage.includes('API key')) {
            throw new Error("Invalid API key configuration on server. Please contact the administrator.");
          } else if (errorMessage.includes('billing')) {
            throw new Error("OpenAI billing issue on server. Please contact the administrator.");
          } else if (errorMessage.includes('model')) {
            throw new Error(`Model error: ${errorMessage}. Please try a different model.`);
          }
          throw new Error(errorMessage);
        }

        return data;
      };

      let data = await invokeCompletion(requestBody);
      let content = extractOpenAIResponseText(data);

      if (isGpt5Model(model) && !content.trim() && isReasoningTokenExhaustion(data)) {
        console.warn('[OpenAI Lib - Proxy] GPT-5 exhausted completion budget on reasoning tokens; retrying with lower reasoning effort and higher token budget.');
        data = await invokeCompletion({
          ...requestBody,
          reasoning_effort: 'minimal',
          max_tokens: Math.max(Number(requestBody.max_tokens) || 0, 3000),
        });
        content = extractOpenAIResponseText(data);
      }

      console.log("Response received from OpenAI via proxy");

      if (!content.trim()) {
        console.warn('[OpenAI Lib - Proxy] OpenAI returned an empty text payload', data);
      }

      return {
        content,
        tokensUsed: data.usage?.total_tokens || data.tokensUsed || 0,
      };
    } catch (error: any) {
      lastError = error;
      const isRetryableError = 
        error.message.includes('temporarily unavailable') || 
        error.message.includes('Network') || 
        error.message.includes('service') ||
        error.message.includes('timeout') ||
        error.message.includes('429') || 
        error.message.includes('503') || 
        error.name === 'AbortError' ||
        error.message.includes('fetch');
      
      if (isRetryableError && retries > 1) {
        console.warn(`Retrying OpenAI API call, ${retries - 1} attempts remaining. Error: ${error.message}`);
        retries--;
        const jitter = Math.random() * 0.3 * backoffDelay;
        await new Promise(resolve => setTimeout(resolve, backoffDelay + jitter));
        backoffDelay *= 2;
      } else {
        throw error;
      }
    }
  }
  throw lastError || new Error("Failed to get response from OpenAI after multiple attempts.");
}

// Function to check if the OpenAI integration is working
export async function checkOpenAIConnection(): Promise<boolean> {
  console.log("Checking OpenAI connection with server-side key via proxy.");
  try {
    const { data, error } = await supabase.functions.invoke('openai-proxy', {
      body: {
        endpoint: 'models',
        body: {} // Empty body for listing models
      }
    });
    if (error || data.error) {
      console.error('Error checking OpenAI connection via proxy:', error || data.error);
      return false;
    }
    return true;
  } catch (error) {
    console.error('Error checking OpenAI connection via proxy:', error);
    return false;
  }
}